import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Download, MessageCircle, CheckCircle, XCircle, ShoppingCart, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canSeeAmount } from "@/lib/roles";
import { downloadQuotationPDF } from "@/lib/pdf";
import usePageTitle from "@/hooks/use-page-title";
import { fmtDate, fmtDateTime } from "@/lib/date";

const STATUS_COLORS = {
  draft: "#71717A", sent: "#0EA5E9", approved: "#22C55E",
  rejected: "#EF4444", converted: "#FF6B00",
};

export default function QuotationDetail() {
  const { id } = useParams();
  usePageTitle(id ? `Quote ${id}` : "Quotation");
  const nav = useNavigate();
  const { user } = useAuth();
  const showAmt = canSeeAmount(user);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSales = user?.role === "sales_executive" || user?.role === "office_staff";
  const isCust = user?.role === "customer";
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [discountAmt, setDiscountAmt] = useState("");

  const load = () => api.get(`/quotations/${id}`).then((r) => setQ(r.data));
  useEffect(() => { load(); }, [id]);

  if (!q) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const setStatus = async (s) => {
    try { await api.patch(`/quotations/${id}/status`, { status: s }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const requestDiscount = async () => {
    try { await api.post(`/quotations/${id}/discount-request`, { amount: Number(discountAmt) || 0 }); setDiscountAmt(""); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const approveDiscount = async (approve) => {
    try { await api.post(`/quotations/${id}/discount-approve?approve=${approve}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };
  const convert = async () => {
    if (!window.confirm("Convert this quotation to a confirmed order?")) return;
    try {
      const { data } = await api.post(`/quotations/${id}/convert`);
      nav(`/orders/${data.order_id}`);
    } catch (e) { setErr(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this quotation permanently?")) return;
    try { await api.delete(`/quotations/${id}`); nav("/quotations"); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const buildWa = async () => {
    await downloadQuotationPDF({ quotation: q });
    const phone = (q.customer_phone || "").replace(/\D/g, "");
    const num = phone ? `91${phone.slice(-10)}` : "";
    const msg = `Hello ${q.customer_name}, please find attached your quotation *${q.id}* — total ₹${Math.round(q.grand_total).toLocaleString("en-IN")}. Reply APPROVE to confirm.\n— Brothers Advertising`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="quotation-detail-root">
      <Link to="/quotations" className="text-sm text-muted-foreground hover:text-[#FF6B00] inline-flex items-center gap-2">
        <ArrowLeft size={14} /> Quotations
      </Link>

      <div className="border-b border-[hsl(var(--border))] pb-6 flex items-end justify-between">
        <div>
          <div className="overline mb-2">Quotation</div>
          <div className="font-mono text-xs text-muted-foreground">{q.id}</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">{q.customer_name}</h1>
          <div className="text-sm text-muted-foreground">{q.customer_company}</div>
        </div>
        <span className="inline-flex items-center gap-2 border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider"
          style={{ borderColor: STATUS_COLORS[q.status], color: STATUS_COLORS[q.status] }} data-testid="quote-status">
          {q.status}
        </span>
      </div>

      <div className="card-swiss p-5 flex flex-wrap gap-2">
        <button onClick={() => downloadQuotationPDF({ quotation: q })} className="btn-swiss btn-outline" data-testid="download-quote-pdf">
          <Download size={14} /> Quotation PDF
        </button>
        <button onClick={buildWa} className="btn-swiss btn-outline"
          style={{ borderColor: "#25D366", color: "#25D366" }} data-testid="share-quote-wa">
          <MessageCircle size={14} /> Share on WhatsApp
        </button>
        {!isCust && q.status === "draft" && (
          <button onClick={() => setStatus("sent")} className="btn-swiss btn-outline" data-testid="mark-sent-btn">Mark as Sent</button>
        )}
        {(isCust || isAdmin) && q.status !== "approved" && q.status !== "converted" && q.status !== "rejected" && (
          <>
            <button onClick={() => setStatus("approved")} className="btn-swiss btn-accent" data-testid="approve-quote-btn">
              <CheckCircle size={14} /> Approve
            </button>
            <button onClick={() => setStatus("rejected")} className="btn-swiss btn-outline"
              style={{ borderColor: "#EF4444", color: "#EF4444" }} data-testid="reject-quote-btn">
              <XCircle size={14} /> Reject
            </button>
          </>
        )}
        {!isCust && q.status === "approved" && !q.order_id && (
          <button onClick={convert} className="btn-swiss btn-accent" data-testid="convert-to-order-btn">
            <ShoppingCart size={14} /> Place Order
          </button>
        )}
        {q.order_id && (
          <Link to={`/orders/${q.order_id}`} className="btn-swiss btn-outline" data-testid="linked-order-link">
            View Order {q.order_id}
          </Link>
        )}
        {isAdmin && (
          <button onClick={remove} className="btn-swiss btn-outline text-red-500 border-red-500" data-testid="delete-quote-btn">
            <Trash2 size={14} /> Delete
          </button>
        )}
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {/* Discount workflow */}
      {!isCust && showAmt && (
        <div className="card-swiss p-5 space-y-3" data-testid="discount-workflow">
          <div className="overline">Discount Approval</div>
          {q.discount_status === "none" && isSales && (
            <div className="flex items-end gap-2">
              <input type="number" placeholder="Discount amount (₹)" value={discountAmt}
                onChange={(e) => setDiscountAmt(e.target.value)}
                className="flex-1 border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none" data-testid="discount-request-input" />
              <button onClick={requestDiscount} disabled={!discountAmt} className="btn-swiss btn-outline" data-testid="request-discount-btn">Request approval</button>
            </div>
          )}
          {q.discount_status === "requested" && (
            <div className="flex items-center gap-2 text-sm">
              <span>Requested: <strong>₹{Math.round(q.discount_requested).toLocaleString("en-IN")}</strong></span>
              {isAdmin && (
                <>
                  <button onClick={() => approveDiscount(true)} className="btn-swiss btn-accent" data-testid="approve-discount-btn">Approve</button>
                  <button onClick={() => approveDiscount(false)} className="btn-swiss btn-outline" data-testid="reject-discount-btn">Reject</button>
                </>
              )}
            </div>
          )}
          {q.discount_status === "approved" && (
            <div className="text-sm text-emerald-600">Discount approved · ₹{Math.round(q.discount_amount).toLocaleString("en-IN")} applied</div>
          )}
          {q.discount_status === "rejected" && <div className="text-sm text-red-500">Discount rejected</div>}
        </div>
      )}

      <div className="card-swiss p-5 space-y-3" data-testid="quote-items-list">
        <div className="overline">Items ({q.items.length})</div>
        {q.items.map((it, i) => (
          <div key={i} className="border border-[hsl(var(--border))] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-sm">{it.product_category}{it.product_name ? ` — ${it.product_name}` : ""}</div>
              {showAmt && <div className="font-mono text-sm">₹{Math.round(it.total || 0).toLocaleString("en-IN")}</div>}
            </div>
            {(it.sizes || []).length > 0 && (
              <table className="w-full text-xs">
                <thead><tr className="text-muted-foreground"><th className="text-left">Size</th><th className="text-right">Qty</th><th className="text-right">Rate</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {it.sizes.map((s, si) => (
                    <tr key={si}>
                      <td>{(s.width && s.height) ? `${s.width}×${s.height} ${s.unit || "ft"}` : "—"}</td>
                      <td className="text-right font-mono">{s.quantity}</td>
                      <td className="text-right font-mono">₹{Math.round(s.rate || 0)}</td>
                      <td className="text-right font-mono">₹{Math.round(s.amount || 0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(it.addons || []).length > 0 && (
              <div className="text-xs text-muted-foreground">Add-ons: {it.addons.map((a) => `${a.name} × ${a.quantity} (₹${Math.round(a.amount || 0)})`).join(", ")}</div>
            )}
            <div className="text-xs text-muted-foreground">GST @ {it.gst_rate || 0}% = ₹{Math.round(it.tax_amount || 0).toLocaleString("en-IN")}</div>
          </div>
        ))}
      </div>

      {showAmt && (
        <div className="card-swiss p-5 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div><div className="overline">Subtotal</div><div className="font-mono">₹{Math.round(q.subtotal).toLocaleString("en-IN")}</div></div>
          <div><div className="overline">GST</div><div className="font-mono">₹{Math.round(q.tax_total).toLocaleString("en-IN")}</div></div>
          <div><div className="overline">Transport</div><div className="font-mono">₹{Math.round(q.transport_charge || 0).toLocaleString("en-IN")}</div></div>
          <div><div className="overline">Install</div><div className="font-mono">₹{Math.round(q.installation_charge || 0).toLocaleString("en-IN")}</div></div>
          <div><div className="overline text-[#FF6B00]">Grand Total</div><div className="font-mono font-black text-lg text-[#FF6B00]">₹{Math.round(q.grand_total).toLocaleString("en-IN")}</div></div>
        </div>
      )}

      {(q.shipping_address || q.remarks) && (
        <div className="card-swiss p-5 space-y-3 text-sm">
          {q.shipping_address && (<div><div className="overline">Shipping</div><div className="whitespace-pre-line mt-1">{q.shipping_address}</div></div>)}
          {q.remarks && (<div><div className="overline">Remarks / Terms</div><div className="whitespace-pre-line mt-1">{q.remarks}</div></div>)}
        </div>
      )}

      <div className="card-swiss p-5">
        <div className="overline mb-3">History</div>
        <div className="space-y-2 text-sm">
          {(q.history || []).slice().reverse().map((h, i) => (
            <div key={i} className="flex justify-between gap-2 border-b border-[hsl(var(--border))] pb-1">
              <div><span className="font-semibold">{h.action}</span> {h.note && <span className="text-muted-foreground">— {h.note}</span>}</div>
              <div className="text-xs text-muted-foreground font-mono">{fmtDateTime(h.at)} · {h.by}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
